import { createPortal } from 'react-dom';
import { useLayoutEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../common/Button';
import FormattedDateInput from '../ui/FormattedDateInput';
import { formatDisplayDate } from '../../utils/dateFormat';
import { todayIsoDate } from '../../utils/reportDates';

type DashboardDateFilterProps = {
  from: string;
  to: string;
  onFromChange: (iso: string) => void;
  onToChange: (iso: string) => void;
  onApply: () => void;
  appliedFrom: string;
  appliedTo: string;
};

export function DashboardDateFilter({
  from,
  to,
  onFromChange,
  onToChange,
  onApply,
  appliedFrom,
  appliedTo,
}: DashboardDateFilterProps) {
  const { t } = useTranslation();
  const fromId = useId();
  const toId = useId();
  const maxDate = todayIsoDate();

  const rangeLabel =
    appliedFrom && appliedTo
      ? t('allocationDashboard.showingRange', {
          from: formatDisplayDate(appliedFrom),
          to: formatDisplayDate(appliedTo),
        })
      : '';

  return (
    <div className="flt-card dash-date-filter">
      <div className="rpt-filter-grid-actions">
        <div className="flt-f">
          <div className="flt-l">{t('allocationDashboard.from')}</div>
          <FormattedDateInput id={fromId} className="flt-inp" value={from} onChange={onFromChange} maxDate={to || maxDate} />
        </div>
        <div className="flt-f">
          <div className="flt-l">{t('allocationDashboard.to')}</div>
          <FormattedDateInput id={toId} className="flt-inp" value={to} onChange={onToChange} minDate={from || undefined} maxDate={maxDate} />
        </div>
        <div className="rpt-filter-actions">
          <Button variant="primary" onClick={onApply}>
            {t('allocationDashboard.apply')}
          </Button>
        </div>
      </div>
      {rangeLabel ? <div className="dash-date-filter__range">{rangeLabel}</div> : null}
    </div>
  );
}

type DashboardInfoTipProps = {
  tooltip: string;
  tipClassName?: string;
};

export function DashboardInfoTip({
  tooltip,
  tipClassName = 'dash-kpi-card__tip',
}: DashboardInfoTipProps) {
  const [open, setOpen] = useState(false);
  const [tipPos, setTipPos] = useState<{ x: number; y: number } | null>(null);
  const [isHoveringTooltip, setIsHoveringTooltip] = useState(false);

  const buttonRef = useRef<HTMLButtonElement>(null);
  const tipRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    if (!open || !buttonRef.current) return;

    const updateTipPosition = () => {
      const rect = buttonRef.current?.getBoundingClientRect();
      if (!rect) return;

      setTipPos({
        x: rect.left + rect.width / 2,
        y: rect.top,
      });
    };

    updateTipPosition();

    const raf = requestAnimationFrame(updateTipPosition);

    window.addEventListener('resize', updateTipPosition);
    window.addEventListener('scroll', updateTipPosition, true);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', updateTipPosition);
      window.removeEventListener('scroll', updateTipPosition, true);
    };
  }, [open]);

  const shouldShowTooltip = open || isHoveringTooltip;

  // Prevent tooltip from overflowing viewport edges
  useLayoutEffect(() => {
    if (!shouldShowTooltip || !tipPos || !tipRef.current) return;

    const margin = 8;
    const el = tipRef.current;

    el.style.left = `${tipPos.x}px`;
    el.style.transform = 'translate(-50%, -100%)';

    const rect = el.getBoundingClientRect();

    const overflowRight = rect.right - (window.innerWidth - margin);
    const overflowLeft = margin - rect.left;

    if (overflowRight > 0) {
      el.style.left = `${tipPos.x - overflowRight}px`;
    } else if (overflowLeft > 0) {
      el.style.left = `${tipPos.x + overflowLeft}px`;
    }
  }, [shouldShowTooltip, tipPos, tooltip]);

  return (
    <span
      className="dash-kpi-card__info-wrap"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      onFocus={() => setOpen(true)}
      onBlur={() => setOpen(false)}
      style={{ display: 'inline-flex' }}
    >
      <button
        ref={buttonRef}
        type="button"
        className={`dash-kpi-card__info${open ? ' is-active' : ''}`}
        aria-label={tooltip}
        aria-expanded={Boolean(shouldShowTooltip)}
      >
        <svg
          width="11"
          height="11"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          aria-hidden="true"
        >
          <circle cx="12" cy="12" r="10" />
          <path d="M12 16v-4M12 8h.01" />
        </svg>
      </button>

      {shouldShowTooltip && tipPos
        ? createPortal(
            <div
              ref={tipRef}
              className={`${tipClassName} is-open`}
              role="tooltip"
              onMouseEnter={() => setIsHoveringTooltip(true)}
              onMouseLeave={() => setIsHoveringTooltip(false)}
              style={{
                position: 'fixed',
                top: tipPos.y - 8,
                left: tipPos.x,
                transform: 'translate(-50%, -100%)',
              }}
            >
              {tooltip}
            </div>,
            document.body,
          )
        : null}
    </span>
  );
}